import { useState, useEffect, memo } from "react";
import { Users, Eye } from "lucide-react";

// Live Vibe Check Counter - Social proof near checkout
// Uses deterministic pseudo-random based on event ID for consistency
const LiveVibeCounter = memo(({ eventId, eventName, variant = "default" }) => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Generate deterministic base count from event ID
  useEffect(() => {
    if (!eventId) return;
    
    // Create a hash from event ID for consistent base number
    const hash = eventId.split('').reduce((acc, char, i) => {
      return acc + char.charCodeAt(0) * (i + 1);
    }, 0);
    
    // Base count between 12-47 (realistic range)
    const baseCount = 12 + (hash % 36);
    setCount(baseCount);
    
    // Simulate occasional +1/-1 fluctuations for "live" feel
    const interval = setInterval(() => {
      setIsAnimating(true);
      setCount(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        const newCount = prev + change;
        // Keep within reasonable range
        return Math.max(8, Math.min(55, newCount));
      });
      setTimeout(() => setIsAnimating(false), 300);
    }, 8000 + Math.random() * 4000); // Random interval 8-12s
    
    return () => clearInterval(interval);
  }, [eventId]);

  if (variant === "compact") {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <div className={`w-1.5 h-1.5 rounded-full bg-accent ${isAnimating ? 'animate-ping' : 'animate-pulse'}`} />
        <span className="font-mono">{count}</span>
        <Eye className="w-3 h-3" />
      </div>
    );
  }

  return (
    <div className="glass rounded-xl p-3 flex items-center gap-3" data-testid="live-vibe-counter">
      {/* Pulsing indicator */}
      <div className="relative">
        <div className={`w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center ${isAnimating ? 'scale-110' : ''} transition-transform`}>
          <Users className="w-5 h-5 text-accent" />
        </div>
        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-accent animate-pulse" />
      </div>
      
      {/* Counter text */}
      <div className="flex-1">
        <div className="flex items-baseline gap-1">
          <span className={`font-mono text-xl font-bold text-foreground transition-all ${isAnimating ? 'text-accent scale-105' : ''}`}>
            {count}
          </span>
          <span className="text-xs text-muted-foreground">others</span>
        </div>
        <p className="text-xs text-muted-foreground leading-tight">
          currently booking trips to this event
        </p>
      </div>
      
      {/* Live badge */}
      <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10">
        <div className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-accent">Live</span>
      </div>
    </div>
  );
});

LiveVibeCounter.displayName = "LiveVibeCounter";

export default LiveVibeCounter;
